import { useRuntimeConfig } from '#imports'
import type { RouteLocationRaw, RouteRecordRaw } from 'vue-router'

import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { generateWebviewRoutes } from '~/scopes/webviews/generateWebviewRoutes'
import redirectIfWebview from '~/scopes/webviews/middleware/redirectIfWebview'

import { CAMPAIGN_SLUG } from './const/config.const'
import { RAW_MATERIALS } from './route-names'

// Home for the active locale. Single definition of "go home" shared by the
// entry redirect and the partial-path redirect.
function homeForLocale(locale: string | string[]): RouteLocationRaw {
  return { name: 'campaign-home', params: { locale } }
}

const componentMeta = { key: CAMPAIGN_SLUG, middleware: [redirectIfWebview] }

export default (): RouteRecordRaw[] => {
  const { FF_ENABLE_RAW_MATERIALS_CAMPAIGN_COUNTRIES = '' } =
    useRuntimeConfig().public
  const { market } = useMarketplace()

  const isEnabled = FF_ENABLE_RAW_MATERIALS_CAMPAIGN_COUNTRIES.includes(
    market.countryCode,
  )

  const rawMaterialsRoutes: RouteRecordRaw[] = [
    {
      path: CAMPAIGN_SLUG,
      component: () =>
        import('@backmarket/nuxt-layer-base/components/RouteRecordWithChildrenOnly/RouteRecordWithChildrenOnly.vue'),
      meta: {
        layout: false,
        routeExists: isEnabled,
      },
      children: [
        {
          name: RAW_MATERIALS.HOME,
          path: '',
          component: () => import('~/scopes/campaign/CampaignHome.vue'),
          meta: componentMeta,
        },
        {
          path: 'impact',
          redirect: (to) => homeForLocale(to.params.locale),
        },
        {
          name: RAW_MATERIALS.IMPACT,
          path: 'impact/:type',
          component: () => import('~/scopes/campaign/CampaignHome.vue'),
          meta: componentMeta,
        },
        {
          name: RAW_MATERIALS.IMPACT_DETAIL,
          path: 'impact/:type/:id',
          component: () => import('~/scopes/campaign/CampaignHome.vue'),
          meta: componentMeta,
        },
        {
          name: RAW_MATERIALS.METAL,
          path: 'metal',
          component: () => import('~/scopes/campaign/CampaignHome.vue'),
          meta: componentMeta,
        },
        {
          name: RAW_MATERIALS.METAL_DETAIL,
          path: 'metal/:id',
          component: () => import('~/scopes/campaign/CampaignHome.vue'),
          meta: componentMeta,
        },
        // Video gallery. The playing video is not a route: it layers
        // on any route via `?watch=<id>` (see useModalState).
        {
          name: RAW_MATERIALS.VIDEO,
          path: 'video',
          component: () => import('~/scopes/campaign/CampaignHome.vue'),
          meta: componentMeta,
        },
        {
          name: RAW_MATERIALS.TAKE_ACTIONS,
          path: 'take-action',
          component: () => import('~/scopes/campaign/CampaignHome.vue'),
          meta: componentMeta,
        },
      ],
    },
  ]

  const webviewRawMaterialsRoutes = generateWebviewRoutes(rawMaterialsRoutes)

  return [...rawMaterialsRoutes, ...webviewRawMaterialsRoutes]
}
